import { ApiFailure } from "@/components/data-display/api-failure";
import { LoadingTable } from "@/components/ui/system-state";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Plus, ChevronDown, RefreshCw, Wallet as WalletIcon } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Table, TableWrapper } from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import { FilterBar, FilterChips, FilterSearch, FilterSelect, matchesAllTerms, type FilterChip } from "@/components/data-display/filter-bar";
import { StatCard } from "@/components/data-display/stat-card";
import { ProviderWalletInventory } from "@/features/wallets/provider-wallet-inventory";
import { WalletRow } from "@/features/wallets/wallet-table";
import { useWallets } from "@/hooks/use-wallets";
import { isMachineNamedWallet } from "@/lib/wallet-display-name";
import { PermissionGate } from "@/components/permission/permission-gate";
import { displayTotal, formatUsdValue, summarizeWalletValuations } from "@/features/valuation/valuation";
import { useValuationSummary } from "@/features/valuation/use-valuation-summary";
import type { Wallet } from "@/types";

function walletHref(w: Wallet): string {
  return `/wallets/${w.id}`;
}

// D15 / WAL-105: a real portfolio (the audit captured 55 rows, ~6,100px of table)
// is paged so the list stays scannable instead of one endless scroll. Client-side
// only — the list endpoint returns the whole set and no paging API is called.
const PAGE_SIZE = 25;

const ALL_CHAINS = "All chains";
const ALL_PROVIDERS = "All Providers";
const ALL_TYPES = "All Types";
const ALL_STATUSES = "All Statuses";

/**
 * ALL-5825 (UX audit D15): the gateway names a wallet's signing route in its own
 * internal vocabulary (`Mesh (Silence Trio 2-of-3)`, `Mesh (VaultCore) - Signing`).
 * Customers choose a capability, not vendor wiring, so the provider filter and
 * the table's Provider column show the capability instead. Only the label
 * changes: option values, filter matching and row data stay on the wire value,
 * and a route this map has not seen keeps its own text rather than being
 * mislabelled.
 */
export const PROVIDER_LABELS: Readonly<Record<string, string>> = {
  "Mesh (Silence Trio 2-of-3)": "Phone co-signed (2-of-3)",
  "Mesh (VaultCore)": "Alloy-managed signing",
  "Mesh (VaultCore) - Signing": "Alloy-managed signing",
  "Mesh (VaultCore) — Signing": "Alloy-managed signing",
};

/** Customer-facing text for a wallet provider route; unknown routes pass through. */
export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

export default function WalletsPage() {
  const { wallets, loading, error, refresh } = useWallets();
  // ALL-5569: the gateway summary carries PriceKit valuations; the list rows do not.
  const { summary: gatewayValuationSummary } = useValuationSummary(!loading && !error);
  const [query, setQuery] = useState("");
  const [chain, setChain] = useState(ALL_CHAINS);
  const [provider, setProvider] = useState(ALL_PROVIDERS);
  const [type, setType] = useState(ALL_TYPES);
  const [status, setStatus] = useState(ALL_STATUSES);
  // ALL-5795: a wallet the signer phone registered (`trio_<hex>`) or that lab tooling created
  // (`Name_<timestamp>`) carries no name a person chose. Those rows are hidden by default so a
  // demo reads as the operator's own wallets, and revealed by the footer toggle.
  const [showMachineWallets, setShowMachineWallets] = useState(false);
  const [page, setPage] = useState(1);
  const [showProviderInventory, setShowProviderInventory] = useState(false);

  const filterOptions = useMemo(() => {
    const chains = new Set<string>();
    const providers = new Set<string>();
    const types = new Set<string>();
    const statuses = new Set<string>();

    for (const w of wallets) {
      if (w.chain) chains.add(w.chain);
      if (w.provider) providers.add(w.provider);
      if (w.type) types.add(w.type.toUpperCase());
      if (w.status) statuses.add(w.status.charAt(0).toUpperCase() + w.status.slice(1));
    }

    const toOptions = (all: string, set: Set<string>, label: (value: string) => string = (value) => value) => [
      { value: all, label: all },
      ...[...set].sort().map((v) => ({ value: v, label: label(v) })),
    ];

    return {
      chains: toOptions(ALL_CHAINS, chains),
      // The option value stays the raw provider (filter matching uses it); only
      // the label a person reads is customer-facing.
      providers: toOptions(ALL_PROVIDERS, providers, providerLabel),
      types: toOptions(ALL_TYPES, types),
      statuses: toOptions(ALL_STATUSES, statuses),
    };
  }, [wallets]);

  /**
   * OPS-02/D15: a type filter whose only real value is the one every loaded wallet
   * already has (e.g. all HOT) offers the operator nothing — selecting it re-renders
   * the same list. Show it only when at least two distinct types are loaded. A type
   * the operator already selected keeps the filter visible, so their chip is never
   * stranded without its control.
   */
  const showTypeFilter = filterOptions.types.length > 2 || type !== ALL_TYPES;

  /**
   * ALL-5795: split the loaded wallets into the ones a person named and the machine-named rest.
   * Nothing is dropped: `wallets` is untouched, the stat cards below still count every one of
   * them, and the hidden rows are rendered again the moment the toggle is expanded.
   */
  const { namedWallets, hiddenWalletCount } = useMemo(() => {
    const named: Wallet[] = [];
    let machineNamed = 0;
    for (const w of wallets) {
      if (isMachineNamedWallet({ name: w.name, storedAlias: w.storedAlias, walletId: w.id })) machineNamed += 1;
      else named.push(w);
    }
    return { namedWallets: named, hiddenWalletCount: machineNamed };
  }, [wallets]);

  // The table and its filters operate on the visible set: the wallets a person named, and every
  // wallet once "Show test and lab wallets" is expanded.
  const listedWallets = showMachineWallets ? wallets : namedWallets;

  /**
   * OPS-02 (UX audit D01): the collapsed toggle can hide *every* wallet — a demo where the only
   * wallets are signer-phone registrations and lab tooling. The table then has no rows with no
   * filter applied by the operator, and "No wallets match your filters." reads as an empty, broken
   * page. This is only that case: the toggle is collapsed, there are wallets to reveal, and none
   * a person named is visible. A populated named set filtered down to nothing is a different fact
   * and keeps its own copy.
   */
  const hiddenWalletsOnly = !showMachineWallets && hiddenWalletCount > 0 && namedWallets.length === 0;
  const hiddenWalletsTitle = `${hiddenWalletCount} test and lab wallet${hiddenWalletCount === 1 ? "" : "s"} hidden`;

  const rows = useMemo(() => {
    return listedWallets.filter((w) => {
      // ALL-5809: every term must match one of the wallet's fields ("sepolia trio" narrows, not widens).
      const matchesQ = matchesAllTerms(query, [w.name, w.id, w.provider, w.provider ? providerLabel(w.provider) : null, w.address, w.storedAlias, w.chain, w.type, w.status]);
      const matchesChain = chain === ALL_CHAINS || w.chain === chain;
      const matchesProvider = provider === ALL_PROVIDERS || w.provider === provider;
      const matchesType = type === ALL_TYPES || w.type === type.toLowerCase();
      const matchesStatus = status === ALL_STATUSES || w.status === status.toLowerCase();
      return matchesQ && matchesChain && matchesProvider && matchesType && matchesStatus;
    });
  }, [listedWallets, query, chain, provider, type, status]);

  // A change to the search, any filter, or the "show test and lab wallets"
  // visibility toggle re-reads the table from the first page: staying on page 3
  // of the previous result would show an empty table whenever the narrowed set
  // is shorter than that.
  useEffect(() => {
    setPage(1);
  }, [query, chain, provider, type, status, showMachineWallets]);

  // Pagination counts the filtered, visible set — `rows` after the filters and
  // the lab toggle — never the raw loaded list.
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const rangeStart = (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, rows.length);
  const showingLabel =
    rows.length === 0 ? "Showing 0 of 0" : `Showing ${rangeStart}–${rangeEnd} of ${rows.length}`;

  const chips: FilterChip[] = [];
  if (chain !== ALL_CHAINS) chips.push({ key: "chain", label: `Chain: ${chain}`, clear: () => setChain(ALL_CHAINS) });
  if (provider !== ALL_PROVIDERS) chips.push({ key: "provider", label: `Provider: ${providerLabel(provider)}`, clear: () => setProvider(ALL_PROVIDERS) });
  if (type !== ALL_TYPES) chips.push({ key: "type", label: `Type: ${type}`, clear: () => setType(ALL_TYPES) });
  if (status !== ALL_STATUSES) chips.push({ key: "status", label: `Status: ${status}`, clear: () => setStatus(ALL_STATUSES) });
  if (query.trim()) chips.push({ key: "q", label: `Search: “${query.trim()}”`, clear: () => setQuery("") });
  const clearAll = () => {
    setChain(ALL_CHAINS); setProvider(ALL_PROVIDERS); setType(ALL_TYPES); setStatus(ALL_STATUSES); setQuery("");
  };

  const stats = useMemo(() => {
    const pending = wallets.filter((w) => w.status === "pending").length;
    const totalAum = gatewayValuationSummary ?? summarizeWalletValuations(wallets);
    const aumUnavailable = totalAum.status === "UNAVAILABLE" || totalAum.status === "UNKNOWN";
    const chains = new Set(wallets.map((w) => w.chain)).size;
    // ALL-5638: a row with no provider route from the gateway is not a
    // provider, so it must not inflate this count.
    const providers = new Set(wallets.map((w) => w.provider).filter(Boolean)).size;
    return [
      { value: String(wallets.length), label: "Total Wallets", hint: pending ? `+${pending} pending` : null, positiveHint: true },
      {
        value: formatUsdValue(aumUnavailable ? null : displayTotal(totalAum).value),
        label: "Total AUM",
        hint: aumUnavailable
          ? "Valuation unavailable"
          : totalAum.unvaluedWalletCount > 0 ? `${totalAum.unvaluedWalletCount} unvalued` : null,
        positiveHint: false,
      },
      { value: String(chains), label: "Chains Active", hint: null, positiveHint: false },
      { value: String(providers), label: "Providers", hint: null, positiveHint: false },
    ];
  }, [wallets, gatewayValuationSummary]);

  return (
    <div className="min-w-0">
      {!loading && !error && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {stats.map((s) => (
            <StatCard key={s.label} {...s} />
          ))}
        </div>
      )}

      <FilterBar className="mb-5">
        <FilterSearch
          value={query}
          onChange={setQuery}
          placeholder="Search name, address, chain, provider…"
          ariaLabel="Search wallets"
        />
        <FilterSelect value={chain} onChange={setChain} options={filterOptions.chains} ariaLabel="Filter by chain" />
        <FilterSelect value={provider} onChange={setProvider} options={filterOptions.providers} ariaLabel="Filter by provider" />
        {showTypeFilter && (
          <FilterSelect value={type} onChange={setType} options={filterOptions.types} ariaLabel="Filter by type" />
        )}
        <FilterSelect value={status} onChange={setStatus} options={filterOptions.statuses} ariaLabel="Filter by status" />
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-ink"
          aria-label="Refresh wallets"
        >
          <RefreshCw className="size-3.5" aria-hidden />
        </button>
        <PermissionGate permission="wallet:create">
          <Link to="/wallets/new" className={buttonVariants({ size: "md" })}>
            <Plus className="size-4" aria-hidden /> Create Wallet
          </Link>
        </PermissionGate>
      </FilterBar>

      <FilterChips chips={chips} onClearAll={clearAll} testId="wallet-filter-chips" />

      {loading && <LoadingTable rows={6} cols={6} label="Loading wallets" />}

      {error && <ApiFailure error={error} subject="your wallets" onRetry={refresh} />}

      {!loading && !error && (
        <TableWrapper>
          <Table className="dense">
            <colgroup>
              <col style={{ width: 160 }} />
              <col style={{ width: 160 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 56 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 44 }} />
            </colgroup>
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Wallet</th>
                <th className="px-4 py-2.5">Chain</th>
                <th className="px-4 py-2.5">Provider</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Balance (USD)</th>
                <th className="px-4 py-2.5">Source</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 w-10" aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((w) => (
                // UX audit D15: the row's Provider column reads the same customer
                // wording as the filter and its chip, so one wallet never reads two
                // ways on the page. Only the copy shown to a person changes; the
                // filter still matches on the raw wire value `w.provider`.
                <WalletRow
                  key={w.id}
                  wallet={w.provider ? { ...w, provider: providerLabel(w.provider) } : w}
                  href={walletHref(w)}
                />
              ))}
            </tbody>
          </Table>
          {rows.length === 0 &&
            (wallets.length === 0 ? (
              // The invitation to create is shown only to a session the backend
              // will actually allow: a read-only role was told to "create your
              // first wallet" by copy it could never act on, so the purpose line
              // is gated with the action, not just the button.
              <PermissionGate
                permission="wallet:create"
                fallback={
                  <EmptyState
                    card
                    className="m-4"
                    icon={<WalletIcon aria-hidden className="size-6 text-muted" />}
                    title="No wallets yet"
                  />
                }
              >
                <EmptyState
                  card
                  className="m-4"
                  icon={<WalletIcon aria-hidden className="size-6 text-muted" />}
                  title="No wallets yet"
                  description="Create your first wallet to hold and move assets."
                  action={
                    <Link to="/wallets/new" className={buttonVariants({ size: "md" })}>
                      <Plus className="size-4" aria-hidden /> Create Wallet
                    </Link>
                  }
                />
              </PermissionGate>
            ) : hiddenWalletsOnly ? (
              // OPS-02: name the hidden wallets and give the operator the reveal, rather than the
              // filter-miss copy that belongs to a set the operator narrowed. The create action
              // stays visible for a session the backend will allow to create one.
              <EmptyState
                card
                className="m-4"
                icon={<WalletIcon aria-hidden className="size-6 text-muted" />}
                title={hiddenWalletsTitle}
                description="These wallets were created by test and lab tooling. Show them to see every wallet in this environment."
                action={
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowMachineWallets(true)}
                      className={buttonVariants({ variant: "secondary", size: "md" })}
                    >
                      <ChevronDown aria-hidden className="size-4" /> Show test and lab wallets
                    </button>
                    <PermissionGate permission="wallet:create">
                      <Link to="/wallets/new" className={buttonVariants({ size: "md" })}>
                        <Plus className="size-4" aria-hidden /> Create Wallet
                      </Link>
                    </PermissionGate>
                  </div>
                }
              />
            ) : (
              <div className="px-4 py-10 text-center text-sm text-muted">No wallets match your filters.</div>
            ))}
          {/* ALL-5795: the collapsed footer toggle. It is shown whenever there is something to
              reveal — including when every wallet is machine-named and the visible set is empty
              — and it stays keyboard operable because it is a real button. */}
          {hiddenWalletCount > 0 && (
            <div className="border-t border-line px-4 py-2.5">
              <button
                type="button"
                onClick={() => setShowMachineWallets((shown) => !shown)}
                aria-expanded={showMachineWallets}
                className="inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
              >
                <ChevronDown
                  aria-hidden
                  className={`size-3.5 transition-transform${showMachineWallets ? " rotate-180" : ""}`}
                />
                {showMachineWallets ? "Hide" : "Show"} test and lab wallets ({hiddenWalletCount})
              </button>
            </div>
          )}
          {/* Paging controls for the visible, filtered set. The "Showing a–b of N"
              window carries the stable `wallets-showing` hook; the shared
              Pagination renders the control group, so the range is stated once. */}
          {rows.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3.5">
              <span data-testid="wallets-showing" className="text-xs font-medium text-muted">
                {showingLabel}
              </span>
              <Pagination
                page={currentPage}
                pageCount={pageCount}
                onPageChange={setPage}
                className="border-t-0 px-0 py-0"
              />
            </div>
          )}
        </TableWrapper>
      )}
      <div className="mt-5 min-w-0 rounded-lg border border-line bg-surface p-4">
        <button
          type="button"
          aria-expanded={showProviderInventory}
          aria-controls="wallet-provider-inventory"
          onClick={() => setShowProviderInventory((shown) => !shown)}
          className={buttonVariants({ variant: "secondary", size: "md" })}
        >
          <ChevronDown aria-hidden className={`size-4${showProviderInventory ? " rotate-180" : ""}`} />
          Provider inventory
        </button>
        <div id="wallet-provider-inventory" className={showProviderInventory ? "mt-4 min-w-0" : undefined}>
          {showProviderInventory && <ProviderWalletInventory />}
        </div>
      </div>
    </div>
  );
}
